import React, { useState, useContext, useEffect, useMemo } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { Sidebar, Menu, Icon, Button, Dropdown, Grid, Popup, DropdownMenu, DropdownItem } from 'semantic-ui-react';

import * as Authentication from '../../../services/Authentication';
import menus from './menu';

import './style.less';
import UserContext from '../../../contexts/UserContext';
import RedirectContext from '../../../contexts/RedirectContext';
import { listInvites } from '../../../services/Invite';
import { Link } from 'react-router-dom';
import { HomeIcon, MenuProfileIcon, DefaultSpeak2Logo, MailStatusIcon } from './HeaderIcons';
import NeighborsAppHeader from './NeighborsAppHeader';
import {
    normalizeNeighborsPath,
} from '../../../neighbors/layout/neighborsInteriorPaths';
import sendToast from '../../../utils/Toast';
import { useFacility } from '../../../neighbors/contexts/facility/FacilityContext';
import { formatResidentLocationParts } from './residentLocationLine';
import { ChangePasswordModal } from '../../pages/profile-settings';
import { deleteRelative } from '../../../services/family';
import DeleteConfirmationModal from '../../../neighbors/components/DeleteConfirmationModal';
import { deleteGuestAccount } from '../../../neighbors/services/Identity';
import { getResidentDisplayFullName } from '../../../neighbors/utils/residentIdentity';

interface InviteInterface {
    _id: string;
    registrantId: string;
    registrantName: string;
    registrantImage?: string;
}

const Header = ({ location, showSidebarProp, isOnNeighborsAppRoute = false }) => {
    const [showSidebar, setShowSidebar] = useState<boolean>(showSidebarProp);
    const [pageName, setPageName] = useState('Speak2 Family');
    const [isManagerRequired, setIsManagerRequired] = useState(false);
    const [showResidentScroll, setShowResidentScroll] = useState(true);

    const { userObject, setUser } = useContext(UserContext);

    const {
        registrants: { list: listOfResidents, activeRegistrantsIndexes },
        profile: relativeProfile
    } = userObject;

    const toggleRedirect = useContext(RedirectContext);
    const [invites, setInvites] = useState<InviteInterface[]>([]);
    const [isSendMessagesPage, setIsSendMessagesPage] = useState(false);
    const [isMultiSelectEnabled, setIsMultiSelectEnabled] = useState(false);
    const [isMultiMode, setIsMultiMode] = useState(false);
    const [multiModeSelectedResidentIndices, setMultiModeSelectedResidentIndices] = useState<number[]>([]);
    const [isChangePasswordModal, setIsChangePasswordModal] = useState<boolean>(false);
    const [confirmDeleteAccount, setConfirmDeleteAccount] = useState<boolean>(false);
    const [isDeletingAccount, setIsDeletingAccount] = useState<boolean>(false);

    const { facilityDetails, loading: isMailInLoading } = useFacility();
    const isMailIn = !!facilityDetails?.isMailIn;
    const isMailInUnavailable = !isMailInLoading && !facilityDetails;
    const facilityTimeZone = (facilityDetails?.FacilityTimeZone as string) || '';
    const activeResident = listOfResidents[activeRegistrantsIndexes[0]]; // get the selected resident to perform resident actions

    const isGuestUser = userObject?.userType === 'guest';
    // Google / Apple / Facebook / Amazon logins carry an identities attribute and have no password to change.
    const isSocialLogin = Boolean(userObject?.cognitoUser?.attributes?.identities);

    // Mirrors the `NeighborsSidenav` mount condition in App.tsx (relationship managers and guests),
    // which is what decides whether its mobile drawer exists at all.
    const hasMobileNav = isGuestUser || !!listOfResidents?.some((registrant) => registrant.manager);

    // Guests have no relatives (Invites and Address Book redirect them home) and social login guests
    // have no password to change.
    const accountSettingOptions = [
        { key: 'profile', text: 'Profile', value: 'profile' },
        { key: 'invites', text: 'Invites', value: 'invites' },
        { key: 'address book', text: 'Address Book', value: 'address book' },
        { key: 'change password', text: 'Change Password', value: 'change password' },
        { key: 'logout', text: 'Log Out', value: 'logout' },
        { key: 'delete-account', text: 'Delete Account', value: 'delete account' },
    ].filter((option) => {
        if (isGuestUser && (option.key === 'invites' || option.key === 'address book')) return false;
        if (isSocialLogin && option.key === 'change password') return false;
        return true;
    })

    const navigate = (to: string) => {
        setShowSidebar(false);
        toggleRedirect(to);
        return;
    };

    // Also resets registrants so a stale resident list can't linger in context after sign-out.
    const buildSignedOutUser = () => ({
        authenticatedUser: undefined,
        profile: undefined,
        authenticated: false,
        registrants: { list: [], activeRegistrantsIndexes: [] },
    });

    const handleLogout = async () => {
        try {
            await Authentication.cognitoSignOut();

            // setUser and the '/login' navigation must land in the SAME render. Done separately
            // (this runs after an `await`, outside React's automatic event-handler batching),
            // there's a tick where the URL still points at the old protected route (e.g.
            // /manage-profiles) but authenticated is already false — that route isn't in the
            // "not a relationship manager" table, so its catch-all `<Redirect from="/*">`
            // (App.tsx) fires and stomps whichever of these two updates applies second.
            unstable_batchedUpdates(() => {
                setUser(buildSignedOutUser());
                toggleRedirect('/login');
            });
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Failed to log out');
        }
    };

    const handleDeleteAccount = async () => {
        setIsDeletingAccount(true);
        try {
            if (isGuestUser) {
                await deleteGuestAccount();
            } else {
                if (!userObject.profile?._id) {
                    sendToast('error', 'User not found');
                    return;
                }
                await deleteRelative(userObject.profile._id);
            }
            // Account is gone server-side. Cognito sign-out is best-effort — always clear local
            // state and send them to /login even if sign-out throws. Do not reload: that would
            // re-authenticate against a deleted profile and strand them in a broken shell.
            try {
                await Authentication.cognitoSignOut();
            } catch {
                // best-effort; local signed-out state below still takes effect
            }
            unstable_batchedUpdates(() => {
                setUser(buildSignedOutUser());
                toggleRedirect('/login');
            });
        } catch (error) {
            sendToast('error', error instanceof Error ? error.message : 'Failed to delete account');
        } finally {
            setIsDeletingAccount(false);
            setConfirmDeleteAccount(false);
        }
    };

    const handleMultiSelectButtonClick = async () => {
        if (isMultiMode) {
            multiModeSelectedResidentIndices.length &&
                setUser({
                    ...userObject,
                    registrants: {
                        ...userObject.registrants,
                        activeRegistrantsIndexes: multiModeSelectedResidentIndices,
                    },
                });
            setMultiModeSelectedResidentIndices([]);
            setIsMultiMode(false);
        } else {
            setIsMultiMode(true);
            setMultiModeSelectedResidentIndices([]);
        }
    };

    const getCurrentPageSettings = () => {
        const path = location.pathname;

        const [page] = menus.filter((e) => {
            const pathRegexp = new RegExp(e.regex || e.location);

            return pathRegexp.test(path);
        });

        return page || {};
    };

    useEffect(() => {
        (process.env.REACT_APP_PLATFORM !== 'neighbors') && loadInvites();
    }, []);

    useEffect(() => {
        setShowSidebar(showSidebarProp);
    }, [showSidebarProp]);

    const loadInvites = async () => {
        try {
            if (userObject.profile) {
                // @ts-ignore
                const fetchedInvites = await listInvites(userObject.profile._id);
                setInvites(fetchedInvites);
            }
        } catch (error) {
            console.log('Something went wrong');
        }
    };

    useEffect(() => {
        const settings = getCurrentPageSettings();
        const { showResidentScroll, showToManagerOnly } = settings;
        let { pageName } = settings;

        // pages not in menu (Neighbor-specific routes)
        if (!pageName) {
            const pageNameMap: { [key: string]: string } = {
                '/resident-social': 'Social',
                '/documents': 'Documents',
                '/announcements': 'Announcements',
                '/links': 'Links',
                '/order-status': 'Order',
                '/survey': 'Survey',
                '/menu': 'Menu',
                '/calendar': 'Calendar',
                '/invites': 'Invites',
                '/manage-profiles': 'Manage Profiles',
                '/addressbook': 'Address Book',
                '/community-news': 'Community News',
                '/groups': 'Groups',
                '/dining': 'Dining',
            };
            if (pageNameMap[location.pathname]) {
                pageName = pageNameMap[location.pathname];
            }
        }

        setPageName(pageName);
        setIsManagerRequired(showToManagerOnly || false);
        setShowResidentScroll(showResidentScroll);
    }, [location.pathname]);

    useEffect(() => {
        const handleCalendarMonth = (e: any) => {
            if (location.pathname === '/calendar') {
                setPageName(`${e.detail} Calendar`);
            } else if (location.pathname === '/menu') {
                setPageName(`${e.detail} Menu`);
            }
        };

        const handleOrderStepChange = (e: any) => {
            if (location.pathname === '/create-order') {
                setPageName(e.detail);
            }
        };

        window.addEventListener('calendarMonthChange', handleCalendarMonth);
        window.addEventListener('orderStepChange', handleOrderStepChange);

        return () => {
            window.removeEventListener('calendarMonthChange', handleCalendarMonth);
            window.removeEventListener('orderStepChange', handleOrderStepChange);
        };
    }, [location.pathname]);

    useEffect(() => {
        if (process.env.REACT_APP_PLATFORM === 'community') {
            // const activityDetailPageRegex = /^\/activities\/?.*[a-z0-9A-Z]$/g;
            const requestDetailPageRegex = /^\/requests\/?.*[a-z0-9A-Z]$/g;
            const { pathname } = location;
            if (
                pathname === '/news' ||
                // pathname === '/activities' ||
                pathname === '/archives' ||
                // activityDetailPageRegex.test(pathname) ||
                requestDetailPageRegex.test(pathname)
            ) {
                setIsSendMessagesPage(true);
                setIsMultiSelectEnabled(true);
            } else {
                setIsSendMessagesPage(false);
                setIsMultiSelectEnabled(false);
                (userObject.registrants.activeRegistrantsIndexes[0] === 'all-users' ||
                    userObject.registrants.activeRegistrantsIndexes.length > 1) &&
                    setUser({
                        ...userObject,
                        registrants: { ...userObject.registrants, activeRegistrantsIndexes: [0] },
                    });
            }
        } else {
            const activityDetailPageRegex = /^\/activities\/?.*[a-z0-9A-Z]$/g;
            const requestDetailPageRegex = /^\/requests\/?.*[a-z0-9A-Z]$/g;
            const { pathname } = location;
            if (
                pathname === '/news' ||
                pathname === '/activities' ||
                pathname === '/archives' ||
                activityDetailPageRegex.test(pathname) ||
                requestDetailPageRegex.test(pathname)
            ) {
                setIsSendMessagesPage(true);
                setIsMultiSelectEnabled(true);
            } else {
                setIsSendMessagesPage(false);
                setIsMultiSelectEnabled(false);
                (userObject.registrants.activeRegistrantsIndexes[0] === 'all-users' ||
                    userObject.registrants.activeRegistrantsIndexes.length > 1) &&
                    setUser({
                        ...userObject,
                        registrants: { ...userObject.registrants, activeRegistrantsIndexes: [0] },
                    });
            }
        }
    }, [location.pathname]);

    const handleManageResidents = () => {
        navigate('/manage-profiles');
    };

    const handleAccountSettingDropdownChange = async (action: string) => {
        if (action === 'profile') {
            navigate('/manage-profiles');
        } else if (action === 'invites') {
            navigate('/invites');
        } else if (action === 'address book') {
            navigate('/addressbook');
        } else if (action === 'change password') {
            setIsChangePasswordModal(true);
        } else if (action === 'logout') {
            await handleLogout();
        } else if (action === 'delete account') {
            setConfirmDeleteAccount(true);
        }
    };

    const ProfileHeader = ({ userObject, openSideBar, pageName }) => {
        return (
            <div className="headerToolbar">
                <Button icon="bars" circular onClick={() => openSideBar()} />
                <span className="page-name">{pageName}</span>
                <div className="right">
                    <span className={`username ${invites.length && 'has-invites'}`}>
                        <Dropdown text={`${userObject.profile.FirstName}`}>
                            <Dropdown.Menu className="left">
                                <Dropdown.Item onClick={() => navigate('/profile-settings')} text="Profile Settings" />
                                <Dropdown.Item onClick={() => navigate('/invites')}>
                                    Invites
                                    <span className={`${!!invites.length ? 'invite-count' : 'no-display'}`}>
                                        {invites.length}
                                    </span>
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={handleLogout} text="Log Out" />
                            </Dropdown.Menu>
                        </Dropdown>
                    </span>
                </div>
            </div>
        );
    };

    const ConnectedRegistrantsList = ({ userObject, isManagerRequired }) => {
        if (!userObject.registrants || !userObject.registrants.list.length) return null;

        return userObject.registrants.list.map((registrant: any, index) => {
            if ((!registrant.manager && isManagerRequired) || !showResidentScroll) {
                return (
                    <div className="name-image-container">
                        <div className={`resident-image-container`} key={index}>
                            {registrant.Image ? (
                                <div
                                    className="resident-image resident-image-bk"
                                    style={{
                                        background: `url(${registrant.Image}) no-repeat`,
                                        backgroundPosition: 'center',
                                    }}
                                />
                            ) : (
                                <div className="relative-avatar">
                                    {registrant && !!registrant.FirstName && registrant.FirstName.charAt(0)}
                                    {registrant && !!registrant.LastName && registrant.LastName.charAt(0)}
                                </div>
                            )}
                            <div className="image-dimmer"></div>
                        </div>
                        <div data-tooltip={`${registrant.FirstName} ${registrant.LastName}`} data-position="top left">
                            <div className="registrant-name-header">
                                {registrant && `${registrant.FirstName} ${registrant.LastName}`}
                            </div>
                        </div>
                    </div>
                );
            }
            return (
                <div
                    key={registrant._id}
                    className={`name-image-container ${pageName === 'Activities' && !registrant.canRecieveRequests && !registrant.manager
                            ? 'disabled'
                            : registrant.isDisabledForPayment ? 'disabled-for-paid' : ''
                        }`}
                >
                    <div
                        className={`resident-image-container
						${!isMultiMode
                                ? index ===
                                    userObject.registrants.activeRegistrantsIndexes.find((elem) => elem === index)
                                    ? 'active-registrant'
                                    : ''
                                : multiModeSelectedResidentIndices.indexOf(index) !== -1
                                    ? 'active-registrant'
                                    : ''
                            }`}
                        key={index}
                        onClick={() => {
                            if (isMultiMode) {
                                const indexInMultiModeArray = multiModeSelectedResidentIndices.indexOf(index);
                                if (indexInMultiModeArray === -1) {
                                    setMultiModeSelectedResidentIndices((prevIndices) => [...prevIndices, index]);
                                } else {
                                    setMultiModeSelectedResidentIndices((prevIndices) =>
                                        prevIndices.filter((e) => e !== index),
                                    );
                                }
                            } else {
                                setUser({
                                    ...userObject,
                                    registrants: { ...userObject.registrants, activeRegistrantsIndexes: [index] },
                                });
                            }
                        }}
                    >
                        {registrant.Image ? (
                            <div
                                className="resident-image resident-image-bk"
                                style={{
                                    background: `url(${registrant.Image}) no-repeat`,
                                    backgroundPosition: 'center',
                                }}
                            />
                        ) : (
                            <div className="relative-avatar">
                                {registrant && !!registrant.FirstName && registrant.FirstName.charAt(0)}
                                {registrant && !!registrant.LastName && registrant.LastName.charAt(0)}
                            </div>
                        )}
                    </div>
                    <div data-tooltip={`${registrant.FirstName} ${registrant.LastName}`} data-position="top left">
                        <div className="registrant-name-header">
                            {registrant && `${registrant.FirstName} ${registrant.LastName}`}
                        </div>
                    </div>
                </div>
            );
        });
    };

    const AllResidentLink = ({ userObject, isManagerRequired }) => {
        return (
            <div key="all" className="name-image-container">
                <div
                    className={`resident-image-container
						${userObject.registrants.activeRegistrantsIndexes.find((elem) => elem === 'all-users') ? 'active-registrant' : ''}`}
                    key={'all-users'}
                    onClick={() => {
                        if (!isMultiMode) {
                            setUser({
                                ...userObject,
                                registrants: { ...userObject.registrants, activeRegistrantsIndexes: ['all-users'] },
                            });
                        }
                    }}
                >
                    <div className="relative-avatar">
                        <Icon disabled={isMultiMode} name="users" />
                    </div>
                    {isMultiMode && <div className="image-dimmer"></div>}
                </div>
                <div data-tooltip={'All Users'} data-position="top left">
                    <div className="registrant-name-header">All Users</div>
                </div>
            </div>
        );
    };

    const residentDisplayFullName = getResidentDisplayFullName(activeResident);

    const residentLocation = useMemo(() => {
        if (process.env.REACT_APP_PLATFORM !== 'neighbors') return null;
        const parts = formatResidentLocationParts(activeResident, facilityTimeZone, isOnNeighborsAppRoute);
        return {
            line: parts?.fullLocation ?? parts?.shortLocation ?? null,
            parts: parts,
        }
    }, [activeResident, facilityTimeZone, isOnNeighborsAppRoute]);
    
    const normalizedNeighborsPath = normalizeNeighborsPath(location.pathname);
    const isNeighborsHomePath = normalizedNeighborsPath === '/' || normalizedNeighborsPath === '/home';

    return (
        <header className={`header ${isOnNeighborsAppRoute ? 'header--neighbors-app' : ''}`} style={{ padding: process.env.REACT_APP_PLATFORM === "neighbors" && !isOnNeighborsAppRoute ? "15px 0 10px 0" : "" }}>
            {isOnNeighborsAppRoute ? (
                <NeighborsAppHeader
                    residentFullName={residentDisplayFullName}
                    location={residentLocation?.parts || null}
                    activeResident={activeResident}
                    isMailIn={!!isMailIn}
                    isMailInLoading={isMailInLoading}
                    isMailInUnavailable={isMailInUnavailable}
                    onAccountSettingChange={handleAccountSettingDropdownChange}
                    hasMobileNav={hasMobileNav}
                />
            ) : process.env.REACT_APP_PLATFORM === "neighbors" ?
                <Grid padded style={{ position: 'relative', justifyContent: 'center', padding: '0' }}>
                    <Grid.Row style={{ paddingBottom: 0, padding: '0' }}>
                        <Grid.Column width={2} style={{ padding: '10px 0 0 14px' }}>
                            <div style={{ width: '100%', maxWidth: '12.6875rem', display: 'flex', justifyContent: 'center' }}>
                                <Link to={'/home'} className="header-icon-hover" style={{ display: 'flex', flexShrink: 0, width: 91, height: 71 }} aria-label="Home">
                                    <HomeIcon width={91} height={71} style={{ display: 'block' }} />
                                </Link>
                            </div>
                        </Grid.Column>
                        <Grid.Column width={14} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', paddingRight: '41px', position: 'static' }}>
                            <div style={{ position: 'absolute', left: '50%', transform: 'translate(-50%, -50%)', top: '50%' }}>
                                {isNeighborsHomePath || !pageName ? (
                                    <Link to={'/home'} className="header-icon-hover" style={{ display: 'block' }} aria-label="Speak 2 Neighbors">
                                        <DefaultSpeak2Logo style={{ height: '70px', width: 'auto' }} />
                                    </Link>
                                ) : (
                                    <div
                                        style={{
                                            textAlign: 'center',
                                            justifyContent: 'center',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            color: '#000',
                                            fontSize: 36,
                                            fontFamily: 'Inter',
                                            fontWeight: 700,
                                            wordWrap: 'break-word',
                                            lineHeight: 'normal',
                                            whiteSpace: 'nowrap',
                                        } as React.CSSProperties}
                                    >
                                        {pageName}
                                    </div>
                                )}
                            </div>
                            {activeResident && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexShrink: 0 }}>
                                    <Popup
                                        content={
                                            isMailInLoading
                                                ? 'Loading mail status...'
                                                : isMailInUnavailable
                                                    ? 'Unable to load mail status.'
                                                    : isMailIn
                                                        ? 'The mail has arrived.'
                                                        : "The mail hasn't arrived yet today."
                                        }
                                        trigger={
                                            <span style={{ display: 'inline-flex', lineHeight: 0 }}>
                                                <MailStatusIcon
                                                    isMailIn={!!isMailIn}
                                                    isMailInLoading={isMailInLoading}
                                                    isMailInUnavailable={isMailInUnavailable}
                                                />
                                            </span>
                                        }
                                        on="hover"
                                        size="small"
                                        position="bottom center"
                                    />
                                    <Popup
                                        content="Manage Profiles"
                                        trigger={
                                            <div
                                                className="welcome-text"
                                                role="button"
                                                tabIndex={0}
                                                onClick={handleManageResidents}
                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleManageResidents(); } }}
                                                style={{
                                                    textAlign: 'center',
                                                    justifyContent: 'center',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    color: '#000',
                                                    fontSize: '24px',
                                                    fontFamily: 'Lato',
                                                    fontWeight: 600,
                                                    fontStyle: 'normal',
                                                    lineHeight: 'normal',
                                                    wordWrap: 'break-word',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <span>Welcome {residentDisplayFullName}!</span>
                                                {residentLocation?.line && !isOnNeighborsAppRoute && (
                                                    <span
                                                        style={{
                                                            marginTop: 4,
                                                            fontSize: 14,
                                                            fontWeight: 400,
                                                            color: '#444',
                                                            lineHeight: 1.3,
                                                        }}
                                                    >
                                                        {residentLocation.line}
                                                    </span>
                                                )}
                                            </div>
                                        }
                                        size="tiny"
                                        position="bottom center"
                                    />
                            <Dropdown
                                icon={null}
                                trigger={
                                    <div
                                        className="header-icon-hover"
                                        style={{
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: 54,
                                            height: 62,
                                            overflow: 'hidden',
                                            background: 'transparent',
                                        }}
                                    >
                                        <MenuProfileIcon width={54} height={62} style={{ display: 'block' }} />
                                    </div>
                                }
                                floating
                            >
                                <DropdownMenu direction='left'>
                                    {accountSettingOptions.map((option) => (
                                        <DropdownItem
                                            key={option.key}
                                            onClick={() => handleAccountSettingDropdownChange(option.value)}
                                            style={option.key === 'delete-account' ? { color: '#c0392b' } : undefined}
                                        >
                                            {option.text}
                                        </DropdownItem>
                                    ))}
                                </DropdownMenu>
                            </Dropdown>
                        </div>
                    )}
                        </Grid.Column>
                    </Grid.Row>
                </Grid> : null
            }
            {isOnNeighborsAppRoute && (
                <>
                    <ChangePasswordModal contact={relativeProfile.Username} open={isChangePasswordModal} close={() => setIsChangePasswordModal(false)} />
                    <DeleteConfirmationModal
                        open={confirmDeleteAccount}
                        onClose={() => setConfirmDeleteAccount(false)}
                        onConfirm={handleDeleteAccount}
                        messageToDisplay={
                            isGuestUser
                                ? 'Are you sure you want to delete your guest account? This will permanently remove your account and cancel any active membership. This action cannot be undone.'
                                : 'Are you sure you want to delete your account? This action cannot be undone.'
                        }
                        loading={isDeletingAccount}
                    />
                </>
            )}
            {process.env.REACT_APP_PLATFORM !== "neighbors" && process.env.REACT_APP_PLATFORM !== "members" ? <>
                <div className="resident-links" style={{ maxWidth: "900px", margin: "auto" }}>
                    {isMultiSelectEnabled &&
                        (isMultiMode ? (
                            <div key="multi-ok" className="name-image-container">
                                <Button
                                    className="resident-image-container"
                                    icon="check"
                                    onClick={handleMultiSelectButtonClick}
                                    positive
                                    compact
                                ></Button>
                                <div data-tooltip={'Okay'} data-position="top left">
                                    <div className="registrant-name-header">OK</div>
                                </div>
                            </div>
                        ) : (
                            <div key="multi" className="name-image-container">
                                <Button
                                    className="resident-image-container"
                                    icon="add user"
                                    onClick={handleMultiSelectButtonClick}
                                    primary
                                    compact
                                ></Button>
                                <div data-tooltip={'Select multiple users'} data-position="top left">
                                    <div className="registrant-name-header">Multi</div>
                                </div>
                            </div>
                        ))}
                    {isSendMessagesPage && (
                        <AllResidentLink userObject={userObject} isManagerRequired={isManagerRequired} />
                    )}

                    <ConnectedRegistrantsList
                        userObject={userObject}
                        isManagerRequired={isManagerRequired}
                    ></ConnectedRegistrantsList>
                </div>
                <div className="menuBar" style={{ maxWidth: "900px", margin: "auto" }}>
                    <ProfileHeader openSideBar={() => setShowSidebar(true)} userObject={userObject} pageName={pageName} />
                    <Sidebar
                        as={Menu}
                        animation="push"
                        icon="labeled"
                        inverted
                        onHide={() => !showSidebarProp && setShowSidebar(false)}
                        vertical
                        visible={showSidebar}
                        width="thin"
                    >
                        {menus.map((menu) => {
                            if (menu.showToCommunityManagerOnly) {
                                const activeRegistrant = userObject.registrants.list[userObject.registrants.activeRegistrantsIndexes[0]];
                                return activeRegistrant && activeRegistrant.isCommunityManager ? (
                                    <Menu.Item
                                        as="a"
                                        onClick={() => navigate(menu.location)}
                                        key={menu.location}
                                        className={menu.pageName + 'button'}
                                    >
                                        {menu.icon}
                                        {menu.pageName}
                                    </Menu.Item>
                                ) : null;
                            }
                            return (
                                <Menu.Item
                                    as="a"
                                    onClick={() => navigate(menu.location)}
                                    key={menu.location}
                                    className={menu.pageName + 'button'}
                                >
                                    {menu.icon}
                                    {menu.pageName}
                                </Menu.Item>
                            );
                        })}
                    </Sidebar>
                </div></> : <></>}
        </header>
    );
};

export default Header;
